import { useRef } from 'react'

import {
  ActionIcon,
  Text,
  TextInput,
  Radio,
  Textarea,
  Select,
  Tooltip,
} from '@mantine/core'
import { DateInput } from '@mantine/dates'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { AlertTriangle, Check, ExternalLink, Users } from 'lucide-react'

import CarrierAutocomplete from 'src/components/Inputs/CarrierAutocomplete/CarrierAutocomplete'
import ContactAutocomplete from 'src/components/Inputs/CarrierAutocomplete/ContactAutocomplete'
import { RECRUITER_SURFACE } from 'src/components/Inputs/CarrierAutocomplete/contactTelemetry'
import DotAutocomplete from 'src/components/Inputs/CarrierAutocomplete/DotAutocomplete'
import { formatContactCount } from 'src/components/Inputs/CarrierAutocomplete/knownContacts'
import MonthYearInput from 'src/components/Inputs/MonthYearInput/MonthYearInput'
import PhoneInput from 'src/components/Inputs/PhoneInput/PhoneInput'
import {
  EmploymentEquipmentType,
  formatEmploymentEquipmentType,
} from 'src/graphql/types/employmentExperiences'
import { formatPhone } from 'src/lib/phone.utils'
import { buildFmcsaCarrierUrl } from 'src/lib/url.utils'
import PlacesAutocomplete from 'src/pages/ApplicantDetailsPage/tabs/ApplicationTab/components/AddressInput/PlacesAutocomplete'
import MultiSelectInput from 'src/pages/ApplicantDetailsPage/tabs/ApplicationTab/components/MultiSelectInput/MultiSelectInput'

dayjs.extend(utc)

interface FormFieldProps {
  label: string
  required?: boolean
  formProps?: any
  editing?: boolean
  warning?: string
}

export const FieldWarning = ({ warning }: { warning?: string }) =>
  warning ? (
    <Tooltip withArrow multiline w={240} position="left" label={warning}>
      <span aria-label={warning} className="flex cursor-help items-center">
        <AlertTriangle className="h-3.5 w-3.5 text-doubleNickel-warning-500" />
      </span>
    </Tooltip>
  ) : null

interface LinkFieldProps {
  label: string
  value?: string | number | null | undefined
  onClick?: () => void
  editing?: boolean
  formProps?: any
}

interface BooleanQuestionProps {
  label: string
  editing?: boolean
  formProps: {
    value: string
    onChange: (value: string) => void
  }
}

interface FormAddressFieldProps {
  label: string
  formProps: any
  editing?: boolean
}

interface FormEquipmentTypesFieldProps {
  label: string
  formProps: any
  editing?: boolean
}

interface FormCompanyFieldProps {
  required?: boolean
  label: string
  formProps: any
  editing?: boolean
  onSelect?: (company: any) => void
}

interface FormContactFieldProps {
  required?: boolean
  dotNumber: string
  label: string
  formProps: any
  editing?: boolean
  onSelect?: (contact: any) => void
  /**
   * Reachable contacts held for `dotNumber`. When given (and editing with a
   * DOT), a one-line hint under the input says how many there are, or that
   * the fields hold a suggestion. Undefined leaves the hint off, which is what
   * callers that do not track contacts get.
   */
  knownCount?: number
  /** The fields hold the carrier-pick suggestion, untouched. */
  suggested?: boolean
  suggestedSourceLabel?: string | null
  onClearSuggested?: () => void
  /** RUM `surface` for the picker's events. */
  surface?: string
}

export const FormTextareaField = ({
  label,
  required = false,
  editing,
  formProps,
}: FormFieldProps) => (
  <div className="flex flex-row items-center justify-between">
    <Text size="xs" fw={300}>
      {label}{' '}
      {required && <span className="text-doubleNickel-error-500">*</span>}
    </Text>
    {editing ? (
      <div className="w-[250px]">
        <Textarea
          placeholder={`Enter ${label.toLowerCase()}`}
          size="xs"
          {...formProps}
          classNames={{
            input: 'text-xs text-right',
          }}
        />
      </div>
    ) : (
      <Text
        size="xs"
        fw={400}
        className="w-[250px] overflow-auto py-1.5 text-right"
      >
        {formProps.value || '-'}
      </Text>
    )}
  </div>
)

export const FormTextField = ({
  label,
  required = false,
  editing,
  formProps,
  warning,
}: FormFieldProps) => (
  <div className="flex flex-row items-center justify-between">
    <Text size="xs" fw={300}>
      {label}{' '}
      {required && <span className="text-doubleNickel-error-500">*</span>}
    </Text>
    <div className="flex w-[250px] flex-row items-center justify-end gap-1.5">
      <FieldWarning warning={warning} />
      {editing ? (
        <div className="flex-1">
          <TextInput
            placeholder={`Enter ${label.toLowerCase()}`}
            size="xs"
            {...formProps}
            classNames={{
              input: 'text-xs text-right',
            }}
          />
        </div>
      ) : (
        <Text size="xs" fw={400} className="overflow-auto py-1.5 text-right">
          {formProps.value || '-'}
        </Text>
      )}
    </div>
  </div>
)

export const FormDateField = ({
  label,
  required = false,
  editing,
  formProps,
}: FormFieldProps) => (
  <div className="flex flex-row items-center justify-between">
    <Text size="xs" fw={300}>
      {label}{' '}
      {required && <span className="text-doubleNickel-error-500">*</span>}
    </Text>
    {editing ? (
      <div className="w-[250px]">
        <DateInput
          size="xs"
          valueFormat="MM/DD/YYYY"
          {...formProps}
          classNames={{
            input: 'text-xs text-right',
          }}
        />
      </div>
    ) : (
      <Text size="xs" fw={400} className="w-[250px] py-1.5 text-right">
        {formProps.value || '-'}
      </Text>
    )}
  </div>
)

export const FormSelectField = ({
  label,
  required = false,
  editing,
  formProps,
}) => (
  <div className="flex flex-row items-center justify-between">
    <Text size="xs" fw={300}>
      {label}{' '}
      {required && <span className="text-doubleNickel-error-500">*</span>}
    </Text>
    {editing ? (
      <div className="w-[250px]">
        <Select
          size="xs"
          {...formProps}
          classNames={{
            input: 'text-xs text-right',
          }}
        />
      </div>
    ) : (
      <Text size="xs" fw={400} className="w-[250px] py-1.5 text-right">
        {formProps.value || '-'}
      </Text>
    )}
  </div>
)

export const FormMonthYearField = ({
  label,
  required = false,
  editing,
  formProps,
}: FormFieldProps) => {
  const displayValue = (() => {
    if (!formProps.value) {
      return '-'
    }

    const parsedDate = dayjs.utc(formProps.value)

    return parsedDate.isValid() ? parsedDate.format('MM/YYYY') : '-'
  })()

  return (
    <div className="flex flex-row items-center justify-between">
      <Text size="xs" fw={300}>
        {label}{' '}
        {required && <span className="text-doubleNickel-error-500">*</span>}
      </Text>
      {editing ? (
        <div className="w-[250px]">
          <MonthYearInput
            size="xs"
            placeholder="MM/YYYY"
            minDate={formProps.minDate}
            maxDate={formProps.maxDate}
            value={formProps.value}
            onChange={formProps.onChange}
            error={formProps.error}
            disabled={formProps.disabled}
            classNames={{
              input: 'text-xs text-right',
            }}
          />
        </div>
      ) : (
        <Text size="xs" fw={400} className="w-[250px] py-1.5 text-right">
          {displayValue}
        </Text>
      )}
    </div>
  )
}

export const FormPhoneField = ({
  label,
  editing,
  required = false,
  formProps,
  warning,
}: FormFieldProps) => (
  <div className="flex flex-row items-center justify-between">
    <Text size="xs" fw={300}>
      {label}{' '}
      {required && <span className="text-doubleNickel-error-500">*</span>}
    </Text>
    <div className="flex w-[250px] flex-row items-center justify-end gap-1.5">
      <FieldWarning warning={warning} />
      {editing ? (
        <div className="flex-1">
          <PhoneInput
            size="xs"
            hideCountrySelector={true}
            {...formProps}
            classNames={{
              input: 'text-xs text-right',
            }}
          />
        </div>
      ) : (
        <Text size="xs" fw={400} className="py-1.5 text-right">
          {formProps.value ? formatPhone(formProps.value) : '-'}
        </Text>
      )}
    </div>
  </div>
)

export const FormLinkField = ({
  label,
  value,
  onClick,
  editing,
  formProps,
}: LinkFieldProps) => (
  <div className="flex flex-row items-center justify-between">
    <Text size="xs" fw={300}>
      {label}
    </Text>
    {editing ? (
      <div className="w-[250px]">
        <TextInput
          placeholder={`Enter ${label.toLowerCase()}`}
          size="xs"
          {...formProps}
          classNames={{
            input: 'text-xs text-right',
          }}
        />
      </div>
    ) : (
      <div className="flex w-[250px] items-center justify-end gap-2">
        <Text size="xs" fw={300} className="py-1.5">
          {String(value) || '-'}
        </Text>
        {value && (
          <ActionIcon size="xs" variant="subtle" color="gray" onClick={onClick}>
            <ExternalLink size={12} />
          </ActionIcon>
        )}
      </div>
    )}
  </div>
)

export const BooleanQuestion = ({
  label,
  editing,
  formProps,
}: BooleanQuestionProps) => {
  const formatBooleanValue = (value: string) => {
    if (value === 'true') return 'Yes'
    if (value === 'false') return 'No'
    return '-'
  }

  return (
    <div className="flex flex-row items-center justify-between">
      <Text size="xs" fw={300} className="flex-1">
        {label}
      </Text>
      {editing ? (
        <Radio.Group size="xs" {...formProps} className="flex-1">
          <div className="flex justify-end gap-4">
            <Radio value="true" label="Yes" size="xs" icon={Check} />
            <Radio value="false" label="No" size="xs" icon={Check} />
          </div>
        </Radio.Group>
      ) : (
        <Text size="xs" fw={400} className="w-[100px] py-1.5 text-right">
          {formatBooleanValue(formProps.value)}
        </Text>
      )}
    </div>
  )
}

export const FormAddressField = ({
  label,
  formProps,
  editing,
}: FormAddressFieldProps) => {
  const formatAddress = (address: any) => {
    if (!address) return '-'
    const parts = [
      address.street,
      address.city,
      address.state,
      address.zipCode,
    ].filter(Boolean)
    return parts.join(', ') || '-'
  }

  // Create a key from the address to force re-render when address changes
  const addressKey = formProps.value
    ? `${formProps.value.street || ''}-${formProps.value.city || ''}-${
        formProps.value.state || ''
      }-${formProps.value.zipCode || ''}`
    : 'empty'

  return (
    <div className="flex flex-row items-center justify-between">
      <Text size="xs" fw={300}>
        {label}
      </Text>
      {editing ? (
        <div className="w-[250px]">
          <PlacesAutocomplete
            key={addressKey}
            value={formProps.value}
            handleChange={formProps.onChange}
            placeholder="Enter address"
            classNames={{
              input: 'text-xs text-right',
            }}
            size="xs"
          />
        </div>
      ) : (
        <Text
          size="xs"
          fw={400}
          className="w-[250px] overflow-auto py-1.5 text-right"
        >
          {formatAddress(formProps.value)}
        </Text>
      )}
    </div>
  )
}

export const FormEquipmentTypesField = ({
  label,
  formProps,
  editing,
}: FormEquipmentTypesFieldProps) => {
  const equipmentTypes = Object.values(EmploymentEquipmentType).map(
    (value) => ({
      value,
      label: formatEmploymentEquipmentType(value),
    })
  )

  const formatEquipmentTypes = (types: string[]) => {
    if (!types || types.length === 0) return '-'
    return types.map((type) => formatEmploymentEquipmentType(type)).join(', ')
  }

  return (
    <div className="flex flex-row items-center justify-between">
      <Text size="xs" fw={300}>
        {label}
      </Text>
      {editing ? (
        <div className="w-[250px]">
          <MultiSelectInput
            editing={true}
            value={formProps.value || []}
            data={equipmentTypes}
            handleChange={formProps.onChange}
            placeholder="Select equipment types"
            classNames={{
              wrapper: 'bg-white',
              input: 'text-xs text-right',
            }}
            size="xs"
          />
        </div>
      ) : (
        <Text size="xs" fw={400} className="w-[250px] py-1.5 text-right">
          {formatEquipmentTypes(formProps.value)}
        </Text>
      )}
    </div>
  )
}

// The carrier inputs are 250px, too narrow for a result's DOT number, fleet
// size and HQ. Their dropdowns open wider, leftward over the field label.
const CARRIER_DROPDOWN_WIDTH = 360

export const FormCompanyField = ({
  required = false,
  label,
  formProps,
  editing,
  onSelect,
}: FormCompanyFieldProps) => (
  <div className="flex flex-row items-center justify-between">
    <Text size="xs" fw={300}>
      {label}{' '}
      {required && <span className="text-doubleNickel-error-500">*</span>}
    </Text>
    {editing ? (
      <div className="w-[250px]">
        <CarrierAutocomplete
          enableContacts={false}
          surface="dashboard"
          dropdownWidth={CARRIER_DROPDOWN_WIDTH}
          value={formProps.value}
          onChange={formProps.onChange}
          error={formProps.error}
          onSelect={onSelect}
          placeholder={`Enter ${label.toLowerCase()}`}
          size="xs"
          classNames={{
            input: 'text-xs text-right',
            root: 'flex-1',
            label: 'text-xs font-medium text-doubleNickel-gray-700',
          }}
        />
      </div>
    ) : (
      <Text size="xs" fw={400} className="w-[250px] py-1.5 text-right">
        {formProps.value || '-'}
      </Text>
    )}
  </div>
)

const openFmcsaCarrierLookup = (dotNumber: string) => {
  window.open(buildFmcsaCarrierUrl(dotNumber), '_blank', 'noopener,noreferrer')
}

/**
 * DOT number. In read mode the number is a link to the FMCSA carrier lookup,
 * as it is on the VoE tab, so a recruiter checking an employer never has to
 * copy the number out.
 */
export const FormCompanyDotField = ({
  required = false,
  label,
  formProps,
  editing,
  onSelect,
  readModeActions,
}: FormCompanyFieldProps & {
  /** Extra links shown after the FMCSA one when the field is read-only. */
  readModeActions?: React.ReactNode
}) => (
  <div className="flex flex-row items-center justify-between">
    <Text size="xs" fw={300}>
      {label}{' '}
      {required && <span className="text-doubleNickel-error-500">*</span>}
    </Text>
    {editing ? (
      <div className="w-[250px]">
        <DotAutocomplete
          enableContacts={false}
          surface={RECRUITER_SURFACE}
          dropdownWidth={CARRIER_DROPDOWN_WIDTH}
          value={formProps.value}
          onChange={formProps.onChange}
          error={formProps.error}
          onSelect={onSelect}
          placeholder={`Enter ${label.toLowerCase()}`}
          size="xs"
          classNames={{
            input: 'text-xs text-right',
            root: 'flex-1',
            label: 'text-xs font-medium text-doubleNickel-gray-700',
          }}
        />
      </div>
    ) : (
      <div className="flex w-[250px] items-center justify-end gap-2">
        {formProps.value ? (
          <>
            <Text
              size="xs"
              fw={400}
              className="cursor-pointer py-1.5 text-doubleNickel-brand-500 hover:underline"
              onClick={() => openFmcsaCarrierLookup(String(formProps.value))}
            >
              {formProps.value}
            </Text>
            <Tooltip label="Open FMCSA carrier lookup" position="top">
              <ActionIcon
                size="xs"
                variant="subtle"
                color="gray"
                aria-label="Open FMCSA carrier lookup"
                onClick={() => openFmcsaCarrierLookup(String(formProps.value))}
              >
                <ExternalLink size={12} />
              </ActionIcon>
            </Tooltip>
            {readModeActions}
          </>
        ) : (
          <Text size="xs" fw={400} className="py-1.5 text-right">
            -
          </Text>
        )}
      </div>
    )}
  </div>
)

const ContactHint = ({
  dotNumber,
  knownCount,
  suggested,
  suggestedSourceLabel,
  onClearSuggested,
  onOpenContacts,
}: {
  dotNumber: string
  knownCount: number
  suggested?: boolean
  suggestedSourceLabel?: string | null
  onClearSuggested?: () => void
  onOpenContacts?: () => void
}) => (
  <div className="mt-1 flex justify-end">
    <div
      data-testid="contact-hint"
      className="w-[250px] text-right text-[11px] text-doubleNickel-gray-500"
    >
      {suggested ? (
        <>
          Suggested
          {suggestedSourceLabel ? ` · ${suggestedSourceLabel}` : ''}
          {onClearSuggested && (
            <>
              {' · '}
              <button
                type="button"
                onClick={onClearSuggested}
                className="cursor-pointer font-medium text-doubleNickel-brand-500 hover:underline"
              >
                Clear
              </button>
            </>
          )}
        </>
      ) : (
        <>
          {knownCount > 0 && onOpenContacts ? (
            <button
              type="button"
              onClick={onOpenContacts}
              className="inline-flex cursor-pointer items-center gap-1 font-medium text-doubleNickel-brand-500 hover:underline"
            >
              <Users size={11} aria-hidden />
              {formatContactCount(knownCount)}
            </button>
          ) : (
            formatContactCount(knownCount)
          )}
          {` for DOT ${dotNumber}`}
        </>
      )}
    </div>
  </div>
)

export const FormContactField = ({
  required = false,
  dotNumber,
  label,
  formProps,
  editing,
  onSelect,
  knownCount,
  suggested,
  suggestedSourceLabel,
  onClearSuggested,
  surface = RECRUITER_SURFACE,
}: FormContactFieldProps) => {
  const showHint = Boolean(editing && dotNumber && knownCount !== undefined)
  const contactInputRef = useRef<HTMLInputElement>(null)
  const openContacts = () => contactInputRef.current?.focus()

  return (
    <div>
      <div className="flex flex-row items-center justify-between">
        <Text size="xs" fw={300}>
          {label}{' '}
          {required && <span className="text-doubleNickel-error-500">*</span>}
        </Text>
        {editing ? (
          <div className="w-[250px]">
            <ContactAutocomplete
              inputRef={contactInputRef}
              dotNumber={dotNumber}
              surface={surface}
              value={formProps.value}
              onChange={formProps.onChange}
              error={formProps.error}
              onSelect={onSelect}
              size="xs"
              classNames={{
                input: 'text-xs text-right',
                root: 'flex-1',
                label: 'text-xs font-medium text-doubleNickel-gray-700',
              }}
            />
          </div>
        ) : (
          <Text size="xs" fw={400} className="w-[250px] py-1.5 text-right">
            {formProps.value || '-'}
          </Text>
        )}
      </div>
      {showHint && (
        <ContactHint
          dotNumber={dotNumber}
          knownCount={knownCount as number}
          suggested={suggested}
          suggestedSourceLabel={suggestedSourceLabel}
          onClearSuggested={onClearSuggested}
          onOpenContacts={openContacts}
        />
      )}
    </div>
  )
}
